// Thunks extracted from OrganizationResourcesModule.js

import _ from "lodash";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import client from "lib/apolloClient/client";
import {
  createOrganizationResourcesMutation,
  createResourceStructureMutation,
  deleteOrganizationResourcesMutation,
  updateOrganizationResourceMutation,
  updateAttachmentMutation,
  publishResourcesMutation,
  reorderOrganizationResourcesMutation,
} from "../OrganizationResourcesMutations";
import {
  getOrganizationResourcesFromCache,
  writeOrganizationResourcesInCache,
  getChildResourcesFromCache,
  writeChildResourcesInCache,
  writePlannerOrganizationResourcesOfNodeToCache,
} from "../OrganizationResourcesGraphqlHelpers";
import { moveElementInArray } from "Utils";
import { getChildResourcesFeedQuery } from "../OrganizationResourcesQuery";
import { getPlannerOrganizationResourcesOfNodeFromCache } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/UnitPlanGraphqlHelpers";
import { ASSESSMENT } from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { getIsLessonPlanningEnabled } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";

// Thunks
export const updateOrganizationResource = ({
  id,
  label,
  state,
  attachment,
  portalType,
  //shareWithParent = undefined
}) => {
  return async (dispatch, getState) => {
    const currentFlowSection = _.get(
      getState(),
      "unitFlow.currentSection",
      null
    );

    const input = { id, label, state, ...(attachment ? { attachment } : {}) };

    try {
      await client.mutate({
        mutation: updateOrganizationResourceMutation,
        variables: {
          input,
          portalType,
        },
        awaitRefetchQueries: true,
        refetchQueries: currentFlowSection
          ? [
              {
                query: getChildResourcesFeedQuery,
                variables: { id: currentFlowSection, searchText: "" },
              },
            ]
          : [],
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:successfully_with_label",
          locale_params: [
            { key: "label", value: "toastMsgs:updated", isPlainText: false },
          ],
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (error) {
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }

      console.error(error);
    }
  };
};

export const deleteOrganizationResources = params => {
  const {
    ids,
    moduleId,
    moduleType,
    organizationResourcesFeedQueryVariables,
    isV2 = false,
    portalType,
    filters,
  } = params;

  return async (dispatch, getState) => {
    const {
      searchText,
      parentFolderId: parentId,
      types,
      visibleTo,
      orderBy: stateOrderBy,
      orderByDirection: stateOrderByDirection,
      states,
    } = getState().organizationResource.filters;

    const orderBy = params?.orderBy || stateOrderBy;
    const orderByDirection = params?.orderByDirection || stateOrderByDirection;

    const currentFlowSection = _.get(
      getState(),
      "unitFlow.currentSection",
      null
    );

    const isUnitPlanV2 = moduleType === "UNIT_PLAN" && isV2;

    const parentTypeForCache = isUnitPlanV2 ? "UNIT_PLAN_V2" : moduleType;

    const organizationResourcesFromCache = getOrganizationResourcesFromCache({
      parentId: moduleId,
      parentType: parentTypeForCache,
      filters: { searchText, visibleTo, types, filters },
      orderBy,
      orderByDirection,
      ...organizationResourcesFeedQueryVariables,
      ...(isUnitPlanV2 && { isUnitFlow: false }),
    });
    const queryInput = { visibleTo, searchText };

    if (!currentFlowSection) {
      queryInput.filters = {
        states,
      };
    }

    const childResourcesFromCache = getChildResourcesFromCache({
      id: parentId,
      ...queryInput,
      orderBy,
      orderByDirection,
    });

    try {
      await client.mutate({
        mutation: deleteOrganizationResourcesMutation,
        variables: {
          ids: ids,
          portalType,
        },
        awaitRefetchQueries: true,
        refetchQueries: currentFlowSection
          ? [
              {
                query: getChildResourcesFeedQuery,
                variables: {
                  id: currentFlowSection,
                  searchText: "",
                },
              },
            ]
          : [],
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            deleteOrganizationResource: true,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { deleteOrganizationResource },
            },
          }
        ) => {
          if (deleteOrganizationResource) {
            if (!parentId) {
              // As we use v2 query for unitPlan fragment we have to apply some checks externally

              let data = {};

              if (moduleType === "UNIT_PLAN" && isV2) {
                const oldResources = _.get(
                  organizationResourcesFromCache,
                  "organizationResourcesConnection",
                  []
                );
                data = {
                  ...organizationResourcesFromCache,
                  organizationResourcesConnection: {
                    ...oldResources,
                    edges: _.filter(oldResources.edges, edge => {
                      return !_.includes(ids, edge.node.id);
                    }),
                  },
                };
              } else if (moduleType === ASSESSMENT) {
                const oldResources = _.get(
                  organizationResourcesFromCache,
                  "organizationResourcesV2",
                  []
                );

                data = {
                  ...organizationResourcesFromCache,
                  organizationResourcesV2: {
                    ...oldResources,
                    edges: _.filter(oldResources?.edges, edge => {
                      return !_.includes(ids, edge?.node?.id);
                    }),
                  },
                };
              } else {
                const oldResources = _.get(
                  organizationResourcesFromCache,
                  "organizationResources",
                  []
                );
                data = {
                  ...organizationResourcesFromCache,
                  organizationResources: _.filter(
                    oldResources,
                    item => !_.includes(ids, item.id)
                  ),
                };
              }

              setTimeout(() => {
                writeOrganizationResourcesInCache({
                  parentId: moduleId,
                  parentType: parentTypeForCache,
                  filters: { searchText, visibleTo, types, filters },
                  orderBy,
                  orderByDirection,
                  data,
                  ...organizationResourcesFeedQueryVariables,
                  ...(isUnitPlanV2 && { isUnitFlow: false }),
                });
              });
            } else {
              const oldChildResources = _.get(
                childResourcesFromCache,
                "children",
                []
              );
              setTimeout(() => {
                writeChildResourcesInCache({
                  id: parentId,
                  ...queryInput,
                  orderBy,
                  orderByDirection,
                  data: {
                    ...childResourcesFromCache,
                    children: _.filter(
                      oldChildResources,
                      item => !_.includes(ids, item.id)
                    ),
                  },
                  ...organizationResourcesFeedQueryVariables,
                });
              });
            }
          }
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:successfully_with_label",
          locale_params: [
            { key: "label", value: "toastMsgs:deleted", isPlainText: false },
          ],
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (error) {
      console.error(error);
      if (!parentId) {
        setTimeout(() => {
          writeOrganizationResourcesInCache({
            parentId: moduleId,
            parentType: parentTypeForCache,
            filters: { searchText, visibleTo, types },
            data: organizationResourcesFromCache,
            ...(isUnitPlanV2 && { isUnitFlow: false }),
          });
        });
      } else {
        setTimeout(() => {
          writeChildResourcesInCache({
            id: parentId,
            searchText,
            visibleTo,
            types: types,
            data: childResourcesFromCache,
          });
        });
      }
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const createResourceStructureMapping = params => {
  const {
    parentId,
    types,
    visibleTo,
    organizationResources,
    conflictingResourceIds,
    moduleId,
    moduleType,
    tags = [],
    isCountry = false,
    portalType,
  } = params;

  return async (dispatch, getState) => {
    let res = null;

    const mutationVariables = {
      parentId: moduleId,
      parentType: moduleType,
      parentResourceId: parentId,
      // shouldHandleSequence: true,
      // putAfterItemId,
      tags,
      isCountry,
      portalType,
      type: types,
      visibleTo,
      bulkResourcesData: {
        conflictingResourceIds,
        bulkResources: organizationResources,
      },
    };

    try {
      res = await client.mutate({
        mutation: createResourceStructureMutation,
        variables: {
          input: mutationVariables,
        },
      });
      const folderStructure = _.get(
        res,
        "data.platform.createBulkOrganizationResources"
      );
      return folderStructure;
    } catch (error) {
      console.error(error);

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));

      return res;
    }
  };
};

export const createOrganizationResources = params => {
  const {
    parentId,
    types,
    visibleTo,
    organizationResources,
    moduleId,
    moduleType,
    putAfterItemId = null,
    organizationResourcesFeedQueryVariables = {},
    tags = [],
    isCountry = false,
    isV2 = false,
    portalType,
    filters,
    isRightPaneResource = false,
    assessmentType,
  } = params;

  return async (dispatch, getState) => {
    const {
      searchText,
      orderBy: stateOrderBy,
      orderByDirection: stateOrderByDirection,
      states,
    } = getState().organizationResource.filters;

    const orderBy = params?.orderBy || stateOrderBy;
    const orderByDirection = params?.orderByDirection || stateOrderByDirection;

    const shouldProcessFileContentExtraction =
      moduleType === ASSESSMENT &&
      getIsLessonPlanningEnabled(assessmentType) &&
      ACLStore.can("FeatureFlag:LessonPlanningAIRevamp");

    const updatedOrganizationResources = _.map(organizationResources, item => {
      const updatedItem = { ...item, visibleTo: visibleTo, type: types };

      if (shouldProcessFileContentExtraction && _.get(item, "attachment")) {
        updatedItem.attachment = {
          ...updatedItem.attachment,
          metadata: {
            ..._.get(updatedItem, "attachment.metadata", {}),
            shouldProcessFileContentExtraction: true,
          },
        };
      }

      return updatedItem;
    });

    const isUnitPlanV2 = moduleType === "UNIT_PLAN" && isV2;

    const parentTypeForCache = isUnitPlanV2 ? "UNIT_PLAN_V2" : moduleType;

    try {
      return await client.mutate({
        mutation: createOrganizationResourcesMutation,
        variables: {
          parentId: moduleId,
          parentType: moduleType,
          parentResourceId: parentId,
          organizationResources: updatedOrganizationResources,
          shouldHandleSequence: true,
          putAfterItemId,
          tags,
          isCountry,
          portalType,
          isRightPaneResource,
        },
        update: (
          cache,
          {
            data: {
              platform: { createOrganizationResources },
            },
          }
        ) => {
          if (!parentId) {
            const organizationResourcesFromCache = getOrganizationResourcesFromCache(
              {
                parentId: moduleId,
                parentType: parentTypeForCache,
                filters: {
                  searchText,
                  visibleTo,
                  types: types,
                  filters,
                },
                orderBy,
                orderByDirection,
                ...organizationResourcesFeedQueryVariables,
                ...(isUnitPlanV2 && { isUnitFlow: false }),
              }
            );

            // As we use v2 query for unitPlan fragment we have to apply some checks externally
            let data = {};

            if (moduleType === "UNIT_PLAN" && isV2) {
              const oldResources = _.get(
                organizationResourcesFromCache,
                "organizationResourcesConnection",
                []
              );
              data = {
                ...organizationResourcesFromCache,
                organizationResourcesConnection: {
                  ...oldResources,
                  edges: [
                    {
                      __typename: "OrganizationResourceEdge",
                      node: { ...createOrganizationResources[0] },
                    },
                    ...oldResources.edges,
                  ],
                },
              };
            } else if (moduleType === ASSESSMENT) {
              const oldResources = _.get(
                organizationResourcesFromCache,
                "organizationResourcesV2",
                []
              );

              data = {
                ...organizationResourcesFromCache,
                organizationResourcesV2: {
                  ...oldResources,
                  edges: [
                    ..._.get(oldResources, "edges", []),
                    ..._.map(createOrganizationResources, node => {
                      return {
                        node,
                        __typename: "OrganizationResourceEdge",
                      };
                    }),
                  ],
                },
              };
            } else {
              const oldResources = _.get(
                organizationResourcesFromCache,
                "organizationResources",
                []
              );
              data = {
                ...organizationResourcesFromCache,
                organizationResources: _.unionBy(
                  oldResources,
                  createOrganizationResources,
                  "id"
                ),
              };
            }

            writeOrganizationResourcesInCache({
              parentId: moduleId,
              parentType: parentTypeForCache,
              filters: {
                searchText,
                visibleTo,
                types: types,
                filters,
              },
              orderBy,
              orderByDirection,
              data,
              ...organizationResourcesFeedQueryVariables,
              ...(isUnitPlanV2 && { isUnitFlow: false }),
            });
          } else {
            const queryInput = { visibleTo, searchText, filters: { states } };
            const childResourcesFromCache = getChildResourcesFromCache({
              id: parentId,
              ...queryInput,
              orderBy,
              orderByDirection,
            });
            const oldChildResources = _.get(
              childResourcesFromCache,
              "children",
              []
            );

            writeChildResourcesInCache({
              id: parentId,
              ...queryInput,
              orderBy,
              orderByDirection,
              data: {
                ...childResourcesFromCache,
                children: _.unionBy(
                  oldChildResources,
                  createOrganizationResources,
                  "id"
                ),
              },
              ...organizationResourcesFeedQueryVariables,
            });
          }
        },
      });
      // dispatch(
      //   setToastMsg({
      //     msg: "toastMsgs:successfully_with_label",
      //     locale_params: [
      //       { key: "label", value: "toastMsgs:added", isPlainText: false }
      //     ],
      //     type: "success",
      //     position: "toast-bottom-left"
      //   })
      // );
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateAttachment = params => {
  return async (dispatch, getState) => {
    const { portalType, ...inputParams } = params;
    try {
      const response = await client.mutate({
        mutation: updateAttachmentMutation,
        variables: {
          input: inputParams,
          portalType,
        },
      });

      return { ...response, successful: true };
    } catch (e) {
      if (e.networkError) {
        return {
          successful: false,
          errors: ["toastMsgs:no_internet_connection"],
        };
      } else {
        return {
          successful: false,
          errors: ["toastMsgs:something_went_wrong"],
        };
      }
    }
  };
};

export const publishResources = ({ metadata, type }) => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: publishResourcesMutation,
        variables: {
          input: {
            entityType: "RESOURCES",
            metaData: metadata,
          },
        },
      });

      /**
       * if response is true that means job is created else
       * there is a job which is already running.
       */

      const statusResponse = _.get(
        response,
        "data.integration.integrationCreateJobAndTask",
        false
      );

      if (statusResponse) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:publish_in_progress",
            type: "success",
            position: "toast-bottom-left",
          })
        );
      } else {
        dispatch(setToastMsg("toastMsgs:publish_in_progress_take_time"));
      }
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const assessmentOrganizationResourcesReorder = ({
  id,
  putAfterItemId,
  variables,
  fromIndex,
  toIndex,
}) => {
  return async (dispatch, getState) => {
    const nodeDetails = getPlannerOrganizationResourcesOfNodeFromCache(
      variables
    );

    const organizationResources = _.get(
      nodeDetails,
      "organizationResourcesV2.edges",
      []
    );

    const updatedResourcesEdges = moveElementInArray({
      array: organizationResources,
      fromIndex,
      toIndex,
    });

    writePlannerOrganizationResourcesOfNodeToCache({
      variables,
      data: {
        node: {
          ...nodeDetails,
          organizationResourcesV2: {
            ...nodeDetails.organizationResourcesV2,
            edges: updatedResourcesEdges,
          },
        },
      },
    });

    try {
      await client.mutate({
        mutation: reorderOrganizationResourcesMutation,
        variables: {
          id,
          putAfterItemId,
        },
      });
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const removeFiles = ids => {
  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: deleteOrganizationResourcesMutation,
        variables: { ids },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};
